import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { HexColorGeneratorsService } from "src/app/services/hex-color-generators.service";
import {
  ChartConfiguration,
  ChartDataset, // Replaces ChartDataSets
  ChartOptions,
  ChartType,
} from "chart.js";
import { ChartJSComponent, CptWidgetInput } from "../../types/types";
import {
  MULTI_COLORS,
  MULTI_BACKGROUND_COLORS,
} from "../../types/constants";
import { BaseChartDirective } from "ng2-charts";

@Component({
    selector: "app-multi-var-bar-widget",
    templateUrl: "./multi-var-bar-widget.component.html",
    styleUrls: ["./widget.component.scss"],
    standalone: false
})
export class MultiVarBarWidgetComponent implements OnInit, ChartJSComponent {
  @ViewChild("chart") chartContainer?: ElementRef;
  @ViewChild("resizeContainer", { static: false }) resizeContainer?: ElementRef;
  @ViewChild(BaseChartDirective, { static: false }) chart?: BaseChartDirective;

  displayChart: boolean = true;
  private _displayImage: string = '';

  get displayImage(): string {
    return this._displayImage;
  }

  setDisplayImage() {
    if (!this.chart) return;
    this._displayImage = this.chart.toBase64Image();
  }

  updateChartElement(width: number, height: number): void {
    this.displayChart = width > 250;
    if (this.displayChart) return;
    let sizeRatio = height / width;
    width = 400;
    height = width * sizeRatio;
    this.displayChart = false;
    setTimeout(() => {
      let element = this.resizeContainer?.nativeElement;
      if (!element || !this.chart) return;
      element.style.width = `${width}px`;
      element.style.height = `${height}px`;
      this.chart.update();
    }, 100);
  }

  @Input() widgetInput!: CptWidgetInput;
  data: ChartDataset<'bar'>[] = []; // Typed dataset
  options!: ChartOptions<'bar'>;
  type: ChartType = "bar";

  constructor(private _hex: HexColorGeneratorsService) {}

  ngOnInit(): void {
    this.setConfigurationOptions();
    this.setData();
    console.log(this.widgetInput);
    console.log(this.data);
    console.log(this.labels);
    console.log(this.groups);
  }

  get groups() {
    return this.widgetInput.cptWidget.groups;
  }

  get labels() {
    return this.widgetInput.cptWidget["labels"];
  }

  get label() {
    return this.widgetInput.cptWidget.label;
  }

  private setConfigurationOptions(): void {
    this.options = {
      devicePixelRatio: 2.5,
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        onComplete: () => {
          this.setDisplayImage();
        },
      },
      plugins: { // Moved title and legend here
        title: {
          display: true,
          text: `${this.widgetInput.selection.title}: ${this.widgetInput.scopes[0]}`,
          font: {
            family: "'Georgia', serif"
          },
          color: '#333'
        },
        legend: {
          position: "bottom",
          labels: {
            font: {
              size: 8,
              family: "'Arial', sans-serif",
              weight: 'bold'
            },
            color: '#333'
          },
        },
      },
      scales: {
        x: { // Replaces xAxes array
          ticks: {
            color: '#333',
            font: {
              weight: 'bold'
            }
          },
          grid: { // Replaces gridLines
            color: "rgba(0, 0, 0, 0)",
          },
        },
        y: { // Replaces yAxes array
          beginAtZero: true, // Moved from ticks
          ticks: {
            color: '#333',
            font: {
              weight: 'bold'
            },
            callback: (value, index, ticks) => {
              // Ensure value is treated as number
              const label = Number(value);
              return label > 1000000000
                ? label / 1000000000 + "b"
                : label > 1000000
                ? label / 1000000 + "m"
                : label > 10000
                ? label / 1000 + "k"
                : label;
            },
          },
        },
      },
    };
  }

  updateScope(newScope: string) {
    this.widgetInput.scopes[0] = newScope;
    this.setData();
    this.setConfigurationOptions();
    console.log(this.chart);
    this.chart?.update();
  }

  private updateTitle() {
    const title = this.widgetInput.selection.title;
    const scope = this.widgetInput.scopes[0];
    if (this.options.plugins && this.options.plugins.title) {
         this.options.plugins.title.text = `${title}: ${scope}`;
    }
  }

  private async setData(): Promise<void> {
    this.data = [];
    for (let i = 0; i < this.groups.length; i++) {
      let g = this.groups[i];
      let dataset: ChartDataset<'bar'> = {
        label: g.groupLabel,
        // Use null for initialization to be safe with number[] types
        data: new Array(this.groups.length).fill(null), 
        backgroundColor: MULTI_BACKGROUND_COLORS[i % MULTI_BACKGROUND_COLORS.length],
        borderColor: MULTI_COLORS[i % MULTI_COLORS.length],
        hoverBackgroundColor: MULTI_BACKGROUND_COLORS[i % MULTI_BACKGROUND_COLORS.length],
        hoverBorderColor: MULTI_COLORS[i % MULTI_COLORS.length],
        borderWidth: 1,
      };
      this.data.push(dataset);
    }

    for (let group of this.groups) {
      let ds = this.data.find((d) => d.label == group.groupLabel);
      if (!ds) continue;

      let vars = group.valueVarIDs;
      for (let i = 0; i < vars.length; i++) {
        let v = this.widgetInput.variables.find((n) => n._id == vars[i]);
        if (!v) continue;

        let dataStr = v.data[this.widgetInput.scopes[0]];
        // Handle potential undefined/null data
        let val = dataStr ? Number(dataStr.replace(/,/g, "")) : null;
        
        // Ensure data array is populated correctly
        if (ds.data) {
             ds.data[i] = val as number; // Assertion for strict typing
        }
      }
    }
  }
}
